/**
 * useJobList Hook
 *
 * React Query hook for fetching the paginated job list with optional filters.
 * Re-fetches automatically whenever the filter params change.
 */

import { isInFlightWorkOrderStatus } from '@/domains/jobs/constants/inFlightWorkOrderStatus';
import { queryKeys } from '@/lib/query-client';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { JobListParams } from '../api/jobListApi';
import { getJobList } from '../api/jobListApi';

const JOB_LIST_POLL_INTERVAL_MS = 5000;

// CWL-386: poll the list while any visible row is mid-flight (En Route / On
// Site), so a status change surfaces without a manual refresh. Stops the
// moment every row on the page is settled — never polls unconditionally.
export function shouldPollJobList(
  data: { data: { work_order_status: string | null }[] } | undefined,
): boolean {
  return (data?.data ?? []).some((job) => isInFlightWorkOrderStatus(job.work_order_status));
}

export function useJobList(params: JobListParams = {}) {
  const queryKey = queryKeys.jobs.list(params);

  const query = useQuery({
    queryKey,
    queryFn: () => getJobList(params),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    refetchInterval: (q) => (shouldPollJobList(q.state.data) ? JOB_LIST_POLL_INTERVAL_MS : false),
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isPending: query.isPending,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    status: query.status,
  };
}
